import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { DeviceService } from '../services/device-service/device.service';
import { ToastrService } from 'ngx-toastr';
import { MatTabsModule } from '@angular/material/tabs';
import { PeripheralsManagementComponent } from '../peripherals-management/peripherals-management.component';
import { DeviceHistoryComponent } from "../device-history/device-history.component";
import { Router, RouterLink } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { DeviceConfigHistoryComponent } from '../device-config-history/device-config-history.component';
import { OtaHistoryComponent } from '../ota-history/ota-history.component';
import { FunctionAppComponent } from '../function-app/function-app.component';
import { EngineeringComponent } from '../engineering/engineering.component';
import { DeviceHealthComponent } from '../device-health/device-health.component';
import { InstallerLogListComponent } from '../installer-log/installer-log-list/installer-log-list.component';
import { SimDetailsComponent } from './sim-details/sim-details.component';
import { HasPermissionDirective } from '../directives/has-permission.directive';
import { RbacPermissions } from '../services/rbac/rbac-permissions';
import { CanComponentDeactivate } from '../guards/unsaved-changes.guard';
import { UnsavedChangesTrackerService } from '../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../services/unsaved-changes/unsaved-changes-host.helper';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import moment from 'moment';

const UNAVAILABLE = '—';
const EMBEDDED_SIM_TOOLTIP =
  'This manufacturer-provided value reflects the Embedded SIM information recorded during device assembly. Check Device Health for current information.';

@Component({
  selector: 'app-device-details',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatIconModule,
    MatTooltipModule,
    PeripheralsManagementComponent,
    DeviceHistoryComponent,
    RouterLink,
    DeviceConfigHistoryComponent,
    OtaHistoryComponent,
    FunctionAppComponent,
    EngineeringComponent,
    DeviceHealthComponent,
    InstallerLogListComponent,
    SimDetailsComponent,
    HasPermissionDirective,
],
  templateUrl: './device-details.component.html',
  styleUrl: './device-details.component.scss',
})
export class DeviceDetailsComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  readonly RbacPermissions = RbacPermissions;
  readonly unavailable = UNAVAILABLE;
  readonly embeddedSimTooltip = EMBEDDED_SIM_TOOLTIP;

  private readonly unsavedHost = new UnsavedChangesHostHelper(
    inject(UnsavedChangesTrackerService),
    inject(MatDialog),
    inject(Router)
  );

  deviceDetailsData: any;
  deviceId = localStorage.getItem('deviceId');
  gatewayId = localStorage.getItem('gatewayId');
  isLoading = false;

  carrierProfileDisplay = UNAVAILABLE;
  carrierProfileTooltip = '';
  physicalSimDisplay = UNAVAILABLE;
  physicalSimValueTooltip = '';
  embeddedSimDisplay = UNAVAILABLE;
  /** Status/source tooltip on the Embedded SIM value when status is verified; label stays assembly-only. */
  embeddedSimValueTooltip = '';
  configVersionDisplay = UNAVAILABLE;
  configVersionTooltip = '';
  firmwareVersionDisplay = UNAVAILABLE;
  firmwareVersionTooltip = '';
  /** EID of the currently active SIM (pSIM or eSIM), used for TEAL data usage lookup. */
  activeSimEid: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private deviceService: DeviceService,
    private toastr: ToastrService
  ) {}

  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  ngOnDestroy(): void {
    this.unsavedHost.clearOnDestroy();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const deviceId = params['id'] || localStorage.getItem('deviceId');
      if (deviceId) {
        this.loadDeviceDetails(deviceId);
      }
    });
  }

  exportComparison(): void {
    const gatewayId = this.deviceDetailsData?.gatewayId ?? this.gatewayId;
    if (!gatewayId) {
      this.toastr.warning(
        'Gateway ID is not available for this device.',
        'Export Unavailable',
        { timeOut: 5000, closeButton: true, progressBar: true }
      );
      return;
    }

    this.isLoading = true;
    this.deviceService.getExportComparisonDataXL(gatewayId).subscribe({
      next: (blob: Blob) => {
        this.isLoading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `device-comparison-${gatewayId}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success(
          `Comparison report for device <strong>${gatewayId}</strong> downloaded successfully.`,
          'Export Successful',
          { timeOut: 30000, closeButton: true, progressBar: true, enableHtml: true }
        );
      },
      error: (error) => {
        this.isLoading = false;
        const rawError = error?.error;
        if (rawError instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            let msg = 'Export failed. Please try again.';
            try {
              const parsed = JSON.parse(reader.result as string);
              msg = parsed?.message ?? parsed?.error ?? msg;
            } catch {
              msg = (reader.result as string) || msg;
            }
            this.toastr.error(msg, 'Export Failed', {
              timeOut: 8000, closeButton: true, progressBar: true
            });
          };
          reader.readAsText(rawError);
        } else {
          const msg =
            rawError?.message ??
            error?.message ??
            'Export failed. Please try again.';
          this.toastr.error(msg, 'Export Failed', {
            timeOut: 8000, closeButton: true, progressBar: true
          });
        }
      }
    });
  }

  loadDeviceDetails(deviceId: string) {
    this.deviceService.getDeviceDetailsById(deviceId).subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.deviceDetailsData = res.data;
          this.gatewayId = res.data.gatewayId;
          this.applyAssemblySimDisplays(res.data);
          this.loadVerifiedHeaderFacts(res.data.gatewayId, res.data);
        } else {
          console.warn('No data available in the response:', res);
        }
      },
      error: (error: any) => {
        const msg =
          error?.error?.message ??
          error?.message ??
          'Failed to load device details. Please try again.';
        this.toastr.error(msg, 'Load Failed', {
          timeOut: 8000, closeButton: true, progressBar: true
        });
      }
    });
  }

  private applyAssemblySimDisplays(data: any): void {
    this.physicalSimDisplay = this.toAssemblyEidDisplay(data?.psim);
    this.embeddedSimDisplay = this.toAssemblyEidDisplay(data?.esim ?? data?.Esim);
  }

  private toAssemblyEidDisplay(value: unknown): string {
    if (value == null || value === '' || value === 0 || value === '0') {
      return UNAVAILABLE;
    }
    return String(value);
  }

  private loadVerifiedHeaderFacts(gatewayId: string | null | undefined, deviceData: any): void {
    if (!gatewayId) {
      this.resetVerifiedDisplays();
      return;
    }

    forkJoin({
      simComparison: this.deviceService.getSimComparisonOverview(gatewayId).pipe(
        catchError(() => of(null))
      ),
      activeProfile: this.deviceService.getActiveSimProfile(gatewayId).pipe(
        catchError(() => of(null))
      ),
      mongoConfig: this.deviceService.getMongoConfig([gatewayId]).pipe(
        catchError(() => of(null))
      ),
    }).subscribe(({ simComparison, activeProfile, mongoConfig }) => {
      const comparison = simComparison?.data ?? simComparison;
      const profile = activeProfile?.data ?? activeProfile;
      const mongo =
        mongoConfig?.[gatewayId] ??
        mongoConfig?.data?.[gatewayId] ??
        null;

      this.applyCarrierProfile(comparison, profile);
      this.applyVerifiedSimLineStatuses(comparison, profile, deviceData);
      this.applyConfigAndFirmware(mongo, profile);
      this.resolveActiveSimEid(comparison, profile, deviceData);
    });
  }

  private resetVerifiedDisplays(): void {
    this.carrierProfileDisplay = UNAVAILABLE;
    this.carrierProfileTooltip = '';
    this.physicalSimValueTooltip = '';
    this.embeddedSimValueTooltip = '';
    if (this.deviceDetailsData) {
      this.applyAssemblySimDisplays(this.deviceDetailsData);
    }
    this.configVersionDisplay = UNAVAILABLE;
    this.configVersionTooltip = '';
    this.firmwareVersionDisplay = UNAVAILABLE;
    this.firmwareVersionTooltip = '';
  }

  private applyCarrierProfile(comparison: any, profile: any): void {
    const mg14Profile = this.pickMg14Profile(comparison, profile);
    const capturedAt = this.formatCaptureTime(
      comparison?.configLastSeen ?? profile?.configLastSeen ?? profile?.modemLastSeen
    );

    if (mg14Profile) {
      this.carrierProfileDisplay = mg14Profile;
      this.carrierProfileTooltip = capturedAt
        ? `Derived from the IMSI in the latest MG14 configuration data uploaded to MongoDB (captured ${capturedAt} UTC).`
        : 'Derived from the IMSI in the latest MG14 configuration data uploaded to MongoDB.';
      return;
    }

    this.carrierProfileDisplay = UNAVAILABLE;
    this.carrierProfileTooltip =
      'Carrier Profile is unavailable because no verified IMSI was found in the latest MG14 configuration data.';
  }

  private pickMg14Profile(comparison: any, profile: any): string | null {
    const fromActive = profile?.activeSim?.profileName;
    if (this.isPresent(fromActive) && fromActive !== 'Unknown Profile') {
      return String(fromActive);
    }

    const resolved = this.resolveMg14ActiveSlot(comparison, profile, null);
    const fromComparison = resolved?.slot?.profile;
    if (this.isPresent(fromComparison) && fromComparison !== UNAVAILABLE) {
      return String(fromComparison);
    }

    const psimProfile = comparison?.psim?.mg14?.profile;
    if (this.isPresent(psimProfile) && psimProfile !== UNAVAILABLE) {
      return String(psimProfile);
    }

    const esimProfile = comparison?.esim?.mg14?.profile;
    if (this.isPresent(esimProfile) && esimProfile !== UNAVAILABLE) {
      return String(esimProfile);
    }

    return null;
  }

  /**
   * MG14 is source of truth: the modem EID (activeSim.eid / dT=16 did) identifies
   * which slot is currently reporting. Do not use SQL activeSimType for this.
   * Returns which assembly slot matched, plus that slot's MG14 comparison row.
   */
  private resolveMg14ActiveSlot(
    comparison: any,
    profile: any,
    deviceData: any
  ): {
    simType: 'PSIM' | 'ESIM';
    slot: { status?: string; activationStatus?: string; profile?: string };
  } | null {
    const mongoEid = this.normalizeEid(profile?.activeSim?.eid);
    if (!mongoEid) {
      return null;
    }

    const psimEid = this.normalizeEid(
      deviceData?.psim ?? comparison?.psimEid ?? comparison?.psim?.mg14?.eid
    );
    const esimEid = this.normalizeEid(
      deviceData?.esim ?? deviceData?.Esim ?? comparison?.esimEid ?? comparison?.esim?.mg14?.eid
    );

    if (psimEid && psimEid.toLowerCase() === mongoEid.toLowerCase()) {
      const slot = comparison?.psim?.mg14;
      return slot ? { simType: 'PSIM', slot } : null;
    }
    if (esimEid && esimEid.toLowerCase() === mongoEid.toLowerCase()) {
      const slot = comparison?.esim?.mg14;
      return slot ? { simType: 'ESIM', slot } : null;
    }

    return null;
  }

  /**
   * Determines the EID of the active SIM slot and stores it in activeSimEid.
   * Primary: uses resolveMg14ActiveSlot (modem EID from MongoDB as source of truth).
   * Fallback (no MongoDB data): uses comparison.activeSimType from SQL DB to pick
   *   the correct slot. If activeSimType is also absent, defaults to eSIM.
   */
  private resolveActiveSimEid(comparison: any, profile: any, deviceData: any): void {
    const mg14Active = this.resolveMg14ActiveSlot(comparison, profile, deviceData);
    if (mg14Active) {
      if (mg14Active.simType === 'PSIM') {
        this.activeSimEid = this.normalizeEid(
          deviceData?.psim ?? comparison?.psimEid ?? comparison?.psim?.mg14?.eid
        );
      } else {
        this.activeSimEid = this.normalizeEid(
          deviceData?.esim ?? deviceData?.Esim ?? comparison?.esimEid ?? comparison?.esim?.mg14?.eid
        );
      }
      return;
    }

    // MongoDB data absent — fall back to SQL activeSimType from the comparison response
    const activeSimType: string | undefined = comparison?.activeSimType?.toUpperCase();
    if (activeSimType === 'PSIM') {
      this.activeSimEid = this.normalizeEid(
        deviceData?.psim ?? comparison?.psimEid ?? comparison?.psim?.mg14?.eid
      );
    } else {
      // ESIM or unknown — default to eSIM EID (prior behaviour)
      this.activeSimEid = this.normalizeEid(
        deviceData?.esim ?? deviceData?.Esim ?? comparison?.esimEid ?? comparison?.esim?.mg14?.eid
      );
    }
  }

  /**
   * Keep verified status on Physical / Embedded SIM line.
   * Append status only when MG14 modem EID matches that line (MG14 is source of truth).
   */
  private applyVerifiedSimLineStatuses(comparison: any, profile: any, deviceData: any): void {
    this.applyAssemblySimDisplays(deviceData);
    this.physicalSimValueTooltip = '';
    this.embeddedSimValueTooltip = '';

    const capturedAt = this.formatCaptureTime(
      comparison?.configLastSeen ?? profile?.configLastSeen ?? profile?.modemLastSeen
    );

    const mg14Active = this.resolveMg14ActiveSlot(comparison, profile, deviceData);
    if (!mg14Active) {
      return;
    }

    const statusText = this.formatMg14StatusText(mg14Active.slot);
    if (!statusText) {
      return;
    }

    const tooltip = capturedAt
      ? `SIM status from the latest MG14 configuration data uploaded to MongoDB (captured ${capturedAt} UTC).`
      : 'SIM status from the latest MG14 configuration data uploaded to MongoDB.';
    this.appendStatusToSimLine(mg14Active.simType, statusText, tooltip);
  }

  private formatMg14StatusText(slot: {
    status?: string;
    activationStatus?: string;
  }): string | null {
    const status = slot?.status;
    if (!this.isPresent(status) || status === UNAVAILABLE) {
      return null;
    }
    const parts = [String(status)];
    const activation = slot?.activationStatus;
    if (this.isPresent(activation) && activation !== UNAVAILABLE) {
      parts.push(String(activation));
    }
    return parts.join(', ');
  }

  private appendStatusToSimLine(
    simType: 'PSIM' | 'ESIM',
    statusText: string,
    statusTooltip: string
  ): void {
    if (simType === 'PSIM') {
      if (this.physicalSimDisplay === UNAVAILABLE) {
        return;
      }
      this.physicalSimDisplay = `${this.physicalSimDisplay} (${statusText})`;
      this.physicalSimValueTooltip = statusTooltip;
      return;
    }

    if (this.embeddedSimDisplay === UNAVAILABLE) {
      return;
    }
    this.embeddedSimDisplay = `${this.embeddedSimDisplay} (${statusText})`;
    this.embeddedSimValueTooltip =
      `${this.embeddedSimTooltip} ${statusTooltip}`.trim();
  }

  private normalizeEid(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    const text = String(value).trim();
    if (!text || text === '0' || text === UNAVAILABLE) {
      return null;
    }
    return text;
  }

  private applyConfigAndFirmware(mongo: any, profile: any): void {
    const capturedAt = this.formatCaptureTime(
      mongo?.configLastSeen ?? mongo?.gatewayLastSeen ?? profile?.configLastSeen
    );
    const sourceSuffix = capturedAt
      ? ` from the latest MG14 upload to MongoDB (captured ${capturedAt} UTC).`
      : ' from the latest MG14 upload to MongoDB.';

    const configVersion = this.normalizeVersion(mongo?.configVersion);
    if (configVersion) {
      this.configVersionDisplay = configVersion;
      this.configVersionTooltip =
        'Config Version confirmed by the device' + sourceSuffix;
    } else {
      this.configVersionDisplay = UNAVAILABLE;
      this.configVersionTooltip =
        'Config Version is unavailable because it was not confirmed in the latest MG14 upload to MongoDB.';
    }

    const firmwareVersion = this.normalizeVersion(mongo?.firmwareVersion);
    if (firmwareVersion) {
      this.firmwareVersionDisplay = firmwareVersion;
      this.firmwareVersionTooltip =
        'Firmware Version confirmed by the device' + sourceSuffix;
    } else {
      this.firmwareVersionDisplay = UNAVAILABLE;
      this.firmwareVersionTooltip =
        'Firmware Version is unavailable because it was not confirmed in the latest MG14 upload to MongoDB.';
    }
  }

  private normalizeVersion(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    const text = String(value).trim();
    if (!text || text === '0' || text.toLowerCase() === 'null') {
      return null;
    }
    return text;
  }

  private formatCaptureTime(value: unknown): string | null {
    if (!value) {
      return null;
    }
    const m = moment.utc(value as string);
    if (!m.isValid()) {
      return null;
    }
    return m.format('MMM DD, YYYY hh:mm A');
  }

  private isPresent(value: unknown): boolean {
    return value != null && String(value).trim() !== '';
  }
}
